import * as React from 'react'
import type { VariantProps } from 'class-variance-authority'
import { LoaderCircle } from 'lucide-react'
import { Slot } from 'radix-ui'

import { cn } from '@shared/ui/lib/utils'
import { buttonVariants } from './button-variants'
import './button.css'

export interface ButtonProps
  extends React.ComponentProps<'button'>,
    VariantProps<typeof buttonVariants> {
  readonly asChild?: boolean
  readonly fullWidth?: boolean
  readonly isLoading?: boolean
  readonly leadingIcon?: React.ReactNode
  readonly loadingLabel?: React.ReactNode
  readonly mobileFullWidth?: boolean
  readonly trailingIcon?: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button({
  asChild = false,
  children,
  className,
  disabled,
  fullWidth = false,
  isLoading = false,
  leadingIcon,
  loadingLabel,
  mobileFullWidth = false,
  size = 'md',
  trailingIcon,
  type = 'button',
  variant = 'primary',
  ...props
}, ref) {
  const Comp = asChild ? Slot.Root : 'button'
  const isDisabled = disabled || isLoading
  const widthProps = fullWidth
    ? { fullWidth: true, mobileFullWidth: false }
    : { fullWidth: false, mobileFullWidth }

  return (
    <Comp
      aria-disabled={isDisabled || undefined}
      className={cn(
        buttonVariants({
          className,
          fullWidth: widthProps.fullWidth,
          mobileFullWidth: widthProps.mobileFullWidth,
          size,
          variant,
        }),
      )}
      data-loading={isLoading || undefined}
      data-size={size}
      data-slot="button"
      data-variant={variant}
      disabled={asChild ? undefined : isDisabled}
      ref={ref}
      type={asChild ? undefined : type}
      {...props}
    >
      {isLoading ? (
        <LoaderCircle aria-hidden="true" className="animate-spin" />
      ) : (
        leadingIcon
      )}
      <Slot.Slottable>
        {isLoading && loadingLabel ? loadingLabel : children}
      </Slot.Slottable>
      {!isLoading ? trailingIcon : null}
    </Comp>
  )
})

export { Button }

